/**
 * File query + management API functions.
 */

import type { FileDetailResponse } from '@/types/api';
import type { FileUpload } from '@/types/dataSource';

import api from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PaginatedFilesResponse {
  items: FileUpload[];
  total: number;
  total_size_bytes: number;
  page: number;
  page_size: number;
  /** XC-WHO-SEES-WHAT R1: documents these filters match that the caller's role may not see. */
  hidden_for_role?: number;
}

interface DownloadUrlResponse {
  url: string;
  expires_in_minutes: number;
}

interface ListFilesParams {
  page?: number;
  pageSize?: number;
  docClass?: string;
  status?: string;
  // Single ingestion path or comma-separated list, e.g. 'connector,gmail'.
  source?: string;
  search?: string;
  sourceSystem?: string;
  /** Retired files (older versions; removed from Drive): hidden (default) or listed alone. */
  retired?: 'only';
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** List files with pagination and optional filters. */
export async function listFiles(params: ListFilesParams = {}): Promise<PaginatedFilesResponse> {
  const { data } = await api.get('/api/v1/files', {
    params: {
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
      doc_class: params.docClass,
      status: params.status,
      source: params.source,
      source_system: params.sourceSystem || undefined,
      search: params.search || undefined,
      retired: params.retired,
    },
  });
  return data;
}

/** Put a retired file back (Undo): AskWingo answers from it again. Owners and admins only. */
export async function undoRetirement(fileId: string, expectedTipId: string): Promise<void> {
  // The retirement the person saw: the server refuses (409) when it moved since.
  await api.post(`/api/v1/files/${fileId}/retirement`, {
    to: 'current',
    expected_tip_id: expectedTipId,
  });
}

/** Get a single file by ID. */
export async function getFile(fileId: string): Promise<FileUpload> {
  const { data } = await api.get(`/api/v1/files/${fileId}`);
  return data;
}

/** Get a presigned download URL for a file. */
export async function getFileDownloadUrl(fileId: string): Promise<DownloadUrlResponse> {
  const { data } = await api.get(`/api/v1/files/${fileId}/download`);
  return data;
}

/**
 * Fetch raw file content as a blob via a GCS signed URL.
 */
export async function getFileContent(fileId: string): Promise<{ blob: Blob; contentType: string }> {
  const { url } = await getFileDownloadUrl(fileId);
  // Timeout so a stalled GCS fetch surfaces as a query error (and the caller's
  // fallback UI) instead of an eternal loading state.
  const res = await fetch(url, { signal: AbortSignal.timeout(30_000) });
  if (!res.ok) {
    throw new Error(`GCS download failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const contentType = res.headers.get('content-type') ?? blob.type ?? '';
  return { blob, contentType };
}

/** Download a JSON file with pretty-printed formatting. */
export async function downloadPrettyJson(fileId: string, fileName: string): Promise<void> {
  const { data } = await api.get(`/api/v1/files/${fileId}/download/pretty`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Get file detail with extractions for the detail modal. */
export async function getFileDetail(
  fileId: string,
  parseEngine?: string,
): Promise<FileDetailResponse> {
  const params = parseEngine ? { parse_engine: parseEngine } : {};
  const { data } = await api.get(`/api/v1/files/${fileId}/detail`, { params });
  return data;
}

/** Soft-delete a file. */
export async function deleteFile(fileId: string): Promise<FileUpload> {
  const { data } = await api.delete(`/api/v1/files/${fileId}`);
  return data;
}
